/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import uniqueId from 'lodash/uniqueId';
import loadable from '@loadable/component';

import { CDNImgUrl } from '../../utils/CDNImgUrl';

const EnterPasswordParagraph = loadable(
    () => import('../Paragraphs/EnterPasswordParagraph'),
);
const Heading3Paragraph = loadable(
    () => import('../Paragraphs/HeadingParagraph'),
);

const FlexibleHeadingParagraph = loadable(
    () => import('../Paragraphs/FlexibleHeadingParagraph'),
);

const HTMLSourceParagraph = loadable(
    () => import('../Paragraphs/HTMLSourceParagraph'),
);
const ParagraphParagraph = loadable(
    () => import('../Paragraphs/ParagraphParagraph'),
);
const ImageParagraph = loadable(() => import('../Paragraphs/ImageParagraph'));
const VideoParagraph = loadable(() => import('../Paragraphs/VideoParagraph'));
const DocumentParagraph = loadable(
    () => import('../Paragraphs/DocumentParagraph'),
);
const HighlightedLinksParargaph = loadable(
    () => import('../Paragraphs/HighlightedLinksParargaph'),
);
const ExternalLinkBlockParagraph = loadable(
    () => import('../Paragraphs/ExternalLinkBlockParagraph'),
);
const SolutionsBlockParagraph = loadable(
    () => import('../Paragraphs/SolutionsBlockParagraph'),
);
const SolutionsParagraph = loadable(
    () => import('../Paragraphs/SolutionsParagraph/SolutionsParagraph'),
);
const LayoutBlockParagraph = loadable(
    () => import('../Paragraphs/LayoutBlockParagraph'),
);
const CarouselImageGallery = loadable(
    () => import('../Paragraphs/CarouselImageGallery'),
);
const Counter = loadable(() => import('../Paragraphs/counter/Counter'));
const Accordions = loadable(() => import('../Paragraphs/Accordions'));

const ContentBody = ({
    contentBody,
    preview,
    isNewsRoomMainPage,
    langcode,
    frontpageTitle,
    frontpageSubTitle,
    pageContext,
}) => {
    let body = [];

    let layoutNdx = 0;
    const getColumnContents = (columns, contents) => {
        if (contents.length === 0) {
            return [];
        }

        const columnsContens = [[], [], []];

        let colNdx = 0;

        contents.forEach((paragraph) => {
            if (paragraph?.internal?.type === 'paragraph__column_breakpoint') {
                colNdx += 1;
            } else {
                columnsContens[colNdx] = columnsContens[colNdx] || [];
                columnsContens[colNdx] = columnsContens[colNdx].concat(
                    getContentBody(paragraph, null, layoutNdx),
                );
            }
        });

        switch (columns) {
            case 4:
                if (columnsContens.length % 3 === 1) {
                    columnsContens.push([], []);
                } else if (columnsContens.length % 3 === 2) {
                    columnsContens.push([]);
                }
                break;
            default:
                if (columnsContens.length % 2 === 0) {
                    columnsContens.push([]);
                }
                break;
        }

        return columnsContens;
    };

    const getContentBody = (element, index, layoutIndex) => {
        const bodyArr = [];

        if (element?.internal?.type === 'paragraph__text' && element.id) {
            bodyArr.push(
                <ParagraphParagraph
                    element={element}
                    paragraphText={
                        element.fixedValue || element.field_text?.value
                    }
                    langcode={langcode}
                    preview={preview}
                    isNewsRoomMainPage={isNewsRoomMainPage}
                    key={uniqueId()}
                />,
            );
            if (preview) {
                return true;
            }
        } else if (
            element?.internal?.type === 'paragraph__heading_3' &&
            element.field_heading_3 &&
            element.id
        ) {
            bodyArr.push(
                <Heading3Paragraph
                    element={element}
                    paragraphText={element.field_heading_3}
                    key={uniqueId()}
                />,
            );
            if (preview) {
                return true;
            }
        } else if (element?.internal?.type === 'paragraph__heading') {
            bodyArr.push(
                <FlexibleHeadingParagraph data={element} key={uniqueId()} />,
            );

            if (preview) {
                return true;
            }
        } else if (element?.internal?.type === 'paragraph__image' && !preview) {
            const documentPublicURL =
                CDNImgUrl(
                    _get(
                        element,
                        'relationships.field_document.relationships.field_media_document.uri.value',
                    ),
                ) || '';

            bodyArr.push(
                <ImageParagraph
                    element={element}
                    langcode={langcode}
                    pageContext={pageContext}
                    documentPublicURL={documentPublicURL}
                    key={uniqueId()}
                />,
            );
        } else if (
            element?.internal?.type === 'paragraph__carousel_image_gallery' &&
            !preview
        ) {
            bodyArr.push(
                <CarouselImageGallery element={element} key={uniqueId()} />,
            );
        } else if (element?.internal?.type === 'paragraph__video' && !preview) {
            bodyArr.push(<VideoParagraph element={element} key={uniqueId()} />);
        } else if (
            element?.internal?.type === 'paragraph__document' &&
            !preview
        ) {
            bodyArr.push(
                <DocumentParagraph element={element} key={uniqueId()} />,
            );
        } else if (
            element?.internal?.type === 'paragraph__html_source' &&
            !preview
        ) {
            const alignment = _get(
                element,
                'relationships.field_alignments.name',
                '',
            );
            const width = _get(element, 'field_width') || '100';
            const height = _get(element, 'field_height');

            bodyArr.push(
                <HTMLSourceParagraph
                    fieldHtml={element.field_html}
                    alignment={alignment}
                    width={width.replace('%', '')}
                    height={height}
                    id={element.id}
                    langcode={langcode}
                    key={uniqueId()}
                />,
            );
        } else if (
            element?.internal?.type === 'paragraph__counter' &&
            !preview
        ) {
            bodyArr.push([<Counter counter={element} key={uniqueId()} />]);
        } else if (element?.internal?.type === 'paragraph__cards' && !preview) {
            bodyArr.push(
                <SolutionsParagraph
                    element={element}
                    cards={element.relationships.field_card}
                    langcode={langcode}
                    key={uniqueId()}
                />,
            );
            bodyArr.push(
                <HighlightedLinksParargaph cards={element} key={uniqueId()} />,
            );
            bodyArr.push(
                <ExternalLinkBlockParagraph
                    cards={element}
                    langcode={langcode}
                    key={uniqueId()}
                />,
            );
            bodyArr.push(
                <SolutionsBlockParagraph
                    cards={element}
                    langcode={langcode}
                    key={uniqueId()}
                />,
            );
        } else if (element?.internal?.type === 'paragraph__layout') {
            const columns = +_get(element, 'field_layout_buttons') || 0;
            const contents =
                _get(element, 'relationships.field_layout_body') || [];
            const columnsContens = getColumnContents(columns, contents) || [];

            bodyArr.push(
                <LayoutBlockParagraph
                    element={element}
                    columnsContens={columnsContens}
                    columns={columns}
                    key={uniqueId()}
                />,
            );
            layoutNdx += 1;
        } else if (
            element?.internal?.type === 'paragraph__accordion' &&
            !preview
        ) {
            bodyArr.push(<Accordions element={element} />);
        } else if (element?.internal?.type === 'paragraph__enter_password') {
            bodyArr.push(
                <EnterPasswordParagraph
                    errorText={element.field_enter_password}
                    langcode={langcode}
                    pageContext={pageContext}
                    key={uniqueId()}
                />,
            );
        }

        if (isNumber(layoutIndex)) {
            return bodyArr;
        }
        body = body.concat(bodyArr);

        return body;
    };

    const isNumber = (value) => typeof value === 'number' && isFinite(value);

    const renderBody = (content) => {
        const frontPageBody = [];

        content.forEach(getContentBody);

        if (frontpageTitle) {
            frontPageBody.push(
                <h1 className="content-title frontpage-title" key={uniqueId()}>
                    {frontpageTitle}
                </h1>,
            );
        }

        if (frontpageSubTitle) {
            frontPageBody.push(
                <h2 className="content-subtitle">{frontpageSubTitle}</h2>,
            );
        }

        return frontPageBody.concat(body);
    };

    return (
        <div key={`${contentBody.id}_${uniqueId()}`} className="content-body">
            {renderBody(contentBody)}
        </div>
    );
};

export default ContentBody;

ContentBody.propTypes = {
    contentBody: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    preview: PropTypes.bool,
    langcode: PropTypes.string,
    isNewsRoomMainPage: PropTypes.bool,
    frontpageTitle: PropTypes.string,
    frontpageSubTitle: PropTypes.string,
    pageContext: PropTypes.string,
};
